import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { primeAudio } from "@/lib/sound";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatCircleDots, PaperPlaneRight, Warning } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    primeAudio();
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  }

  return (
    <div className="h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative border-r border-border"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.82), rgba(10,10,10,0.94)), url(https://images.pexels.com/photos/6066072/pexels-photo-6066072.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <div className="h-10 w-10 bg-primary flex items-center justify-center rounded-sm">
            <ChatCircleDots size={22} weight="fill" className="text-primary-foreground" />
          </div>
          <span className="text-lg font-semibold tracking-tight">TxtSync</span>
        </div>
        <div className="max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-4">
            UISP · Twilio · SMS
          </p>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">
            Two-way SMS,
            <br />
            synced to your CRM.
          </h1>
          <p className="text-sm text-muted-foreground mt-5 leading-relaxed">
            Pull customers straight from UISP CRM, message them individually or
            blast an entire site, and watch replies land in real time.
          </p>
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          v1.0 · secure operator console
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 bg-primary flex items-center justify-center rounded-sm">
              <ChatCircleDots size={22} weight="fill" className="text-primary-foreground" />
            </div>
            <span className="text-lg font-semibold tracking-tight">TxtSync</span>
          </div>
          <h2 className="text-xl font-medium tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your operator credentials to continue.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                className="rounded-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
                className="rounded-sm"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2 rounded-sm"
              >
                <Warning size={16} weight="fill" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy}
              className="w-full rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-medium gap-2"
            >
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <PaperPlaneRight size={16} weight="fill" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
